import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";

import type { Media } from "@motolynk/api-client";
import { useUiLabels } from "@motolynk/app-core";
import { useTranslation } from "@motolynk/i18n";
import { FILE_RULES, MediaPicker } from "@motolynk/ui";

import { useMediaAccess } from "./access";
import { MEDIA_API, mediaKeys } from "./api";
import { toTile } from "./format";
import { useMediaUpload } from "./useMediaUpload";

export interface MediaPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the chosen files (full objects, with rendition URLs). */
  onPick: (media: Media[]) => void;
  multiple?: boolean;
  kind?: "image" | "document";
  /** Only files the storefront can show: public, and (in the admin) platform media, not vendors' files. */
  storefront?: boolean;
  /** Files already chosen (e.g. the current product gallery). */
  initial?: string[];
}

/**
 * The library picker for any form (products, banners, categories…): search, pick, or upload on the spot.
 * Works in the admin and the vendor panel (each sees its own library).
 *
 *   <MediaPickerDialog open={open} onOpenChange={setOpen} multiple kind="image" onPick={setGallery} />
 */
export function MediaPickerDialog({
  open,
  onOpenChange,
  onPick,
  multiple = false,
  kind,
  initial = [],
  storefront = false,
}: MediaPickerDialogProps) {
  const { t, i18n } = useTranslation("medialib");
  const ui = useUiLabels();
  const { panel, canUpload } = useMediaAccess();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string[]>(initial);
  const [known, setKnown] = useState<Record<string, Media>>({});
  const { uploads, uploadFiles } = useMediaUpload(panel, (media) => {
    setKnown((all) => ({ ...all, [media.uid]: media as unknown as Media }));
    setSelected((current) => (multiple ? [...current, media.uid] : [media.uid]));
  });

  const params = {
    page_size: 24,
    status: "ready" as const,
    ...(kind ? { kind } : {}),
    ...(storefront ? { visibility: "public" as const } : {}),
    ...(storefront && panel === "admin" ? { vendor: "platform" } : {}),
    ...(search.trim() ? { search: search.trim() } : {}),
  };
  const list = useInfiniteQuery({
    queryKey: [...mediaKeys.list(panel, params), "picker"],
    queryFn: async ({ pageParam }) =>
      (await MEDIA_API[panel].list({ ...params, page: pageParam })).data,
    initialPageParam: 1,
    getNextPageParam: (last, all) => (last.next ? all.length + 1 : undefined),
    enabled: open,
  });
  const rows = list.data?.pages.flatMap((p) => p.results) ?? [];
  const byId = { ...known, ...Object.fromEntries(rows.map((m) => [m.uid, m])) };
  const labels = {
    status: (value: string) => t(`status.${value}`),
    trash: t("inTrash"),
    pages: (count: number) => t("pages", { count }),
    video: t("videoLink"),
  };

  return (
    <MediaPicker
      open={open}
      onOpenChange={onOpenChange}
      items={rows.map((m) => toTile(m, labels, i18n.language))}
      loading={list.isFetching}
      search={search}
      onSearchChange={setSearch}
      hasMore={Boolean(list.hasNextPage)}
      onLoadMore={() => void list.fetchNextPage()}
      multiple={multiple}
      selected={selected}
      onSelectedChange={setSelected}
      onConfirm={(ids) => {
        onPick(ids.map((id) => byId[id]).filter((m): m is Media => Boolean(m)));
        onOpenChange(false);
      }}
      onUpload={canUpload ? (files) => void uploadFiles(files) : undefined}
      uploads={uploads}
      rule={
        kind === "document"
          ? FILE_RULES.document
          : kind === "image"
            ? FILE_RULES.image
            : FILE_RULES.imageOrDocument
      }
      labels={{
        title: t("picker.title"),
        search: t("search"),
        select: (title) => t("select", { title }),
        confirm: (count) => (count ? t("picker.confirmCount", { count }) : t("picker.confirm")),
        cancel: ui.cancel,
        loadMore: t("loadMore"),
        empty: t("empty"),
        dropTitle: t("dropTitle"),
        dropHint: t("dropHint"),
        uploading: t("uploading"),
        done: t("uploaded"),
        close: ui.close,
      }}
    />
  );
}
